import React, {useRef, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {NavLink, Outlet, useNavigate} from 'react-router-dom'
import {WetacaLogo} from '../components'
import {trpc} from '../trpc'
import {useSession} from '../auth/useSession'
import {useActiveStore} from '../store/StoreContext'
import {storeCodeSegment} from '../lib/storeRouting'
import {
  LOGIN_PATH,
  ORDERS_BASE_PATH,
  PROPOSAL_BASE_PATH,
  ordersPath,
  proposalPath,
} from '../lib/routes'
import {StoreSwitcher} from '../store/StoreSwitcher'
import {AccountMenu} from './AccountMenu'
import {UnsavedChangesGuard} from './UnsavedChangesGuard'
import {useUnsavedChanges} from './UnsavedChangesContext'
import {useClickAway} from '../hooks/useClickAway'
import styles from './AppShell.module.scss'

/**
 * Authenticated shell — floating top bar plus <main><Outlet/></main>. Mounted
 * once by the layout route in App.tsx around RequireAuth/StoreProvider, so it
 * renders on every guarded route instead of the top bar living inside a page.
 *
 * Desktop: brand + nav (left); store switcher + account avatar (right). Nav
 * labels name the destination screens directly — "Editar pedido" (the weekly
 * proposal) and "Historial" (past orders) — and carry the active store's code so
 * navigation stays within the current store.
 *
 * Mobile: the top bar stays clean (burger + logo + avatar) and the store control
 * drops to its own full-width row below it — the per-page store selector. There
 * is a SINGLE store control (no duplicate "Cambiar tienda" in the burger):
 * StoreSwitcher shows the store inline on desktop and full-width on mobile.
 *
 * The unsaved-changes guard lives here (not inside the proposal page) so it
 * covers route navigation uniformly. Switching store IS a route navigation
 * (StoreContext.switchStore → new /page/:storeCode URL), so the same blocker
 * covers it and no store-switch-specific confirmation exists.
 */
export const AppShell = (): React.ReactElement => {
  const navigate = useNavigate()
  const {user} = useSession()
  const queryClient = useQueryClient()
  const {activeStore, switchStore} = useActiveStore()
  const {isDirty} = useUnsavedChanges()

  const [menuOpen, setMenuOpen] = useState(false)
  const [logoutError, setLogoutError] = useState<string | null>(null)
  const brandRef = useRef<HTMLDivElement>(null)

  const closeMenu = (): void => setMenuOpen(false)
  useClickAway(brandRef, closeMenu, menuOpen)

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      // Purge the ENTIRE cache, not just `auth.me`. Stores, the weekly proposal
      // and the order history are all cached per user; on a shared terminal the
      // next person to log in would otherwise be served the previous one's data
      // until each query went stale.
      queryClient.clear()
      navigate(LOGIN_PATH, {replace: true})
    },
    onError: () => {
      // The session lives in an httpOnly cookie only the server can drop, so a
      // failed logout leaves it alive: navigating to /login would bounce right
      // back through the page's "already authenticated" redirect. Keep the user
      // where they are and tell them the session is still open.
      setLogoutError('No se pudo cerrar la sesión. Comprueba tu conexión e inténtalo de nuevo.')
    },
  })

  // Nav targets keep the active store in the URL. During the brief redirect
  // frames (before a store resolves) fall back to the bare path — RequireStore
  // forwards it to the default store.
  const storeSeg = activeStore !== undefined ? storeCodeSegment(activeStore) : undefined
  const proposalTo = storeSeg !== undefined ? proposalPath(storeSeg) : PROPOSAL_BASE_PATH
  const historyTo = storeSeg !== undefined ? ordersPath(storeSeg) : ORDERS_BASE_PATH

  const navLinkClassName = ({isActive}: {isActive: boolean}): string =>
    ['txt-bold-500', 'txt-decoration-none', styles['app-shell__nav-link'], isActive ? `bg-light-olive ${styles['app-shell__nav-link--active']}` : '']
      .filter(Boolean)
      .join(' ')

  return (
    <div className={`col-flx bg-light-cream ${styles['app-shell']}`}>
      {/* Route navigation + tab close — mounted once so navigating away can't
          tear it down before it blocks. Store switches are route navigations too,
          so this single guard covers them. */}
      <UnsavedChangesGuard when={isDirty} />

      <header className={`row-flx flx-a-c flx-w bg-white ${styles['app-shell__header']}`}>
        <div className={`row-flx flx-a-c m-r-auto modul ${styles['app-shell__brand']}`} ref={brandRef}>
          <button
            type="button"
            className={`flx-j-c flx-gap-4 ${styles['app-shell__burger']}`}
            onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))}
            aria-label="Abrir navegación"
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            <span className={`bg-kale full-width ${styles['app-shell__burger-bar']}`} aria-hidden="true" />
            <span className={`bg-kale full-width ${styles['app-shell__burger-bar']}`} aria-hidden="true" />
            <span className={`bg-kale full-width ${styles['app-shell__burger-bar']}`} aria-hidden="true" />
          </button>

          <WetacaLogo className={`itm-flx-s-0 txt-kale ${styles['app-shell__logo']}`} width={116} height={22} />

          <nav
            className={['flx-gap-4', styles['app-shell__nav'], menuOpen ? styles['app-shell__nav--open'] : '']
              .filter(Boolean)
              .join(' ')}
            aria-label="Navegación principal"
          >
            <NavLink to={proposalTo} className={navLinkClassName} onClick={closeMenu}>
              Editar pedido
            </NavLink>
            <NavLink to={historyTo} className={navLinkClassName} onClick={closeMenu}>
              Historial
            </NavLink>
          </nav>
        </div>

        <div className={`row-flx flx-a-c ${styles['app-shell__account']}`}>
          <AccountMenu
            firstname={user?.firstname ?? ''}
            lastname={user?.lastname ?? ''}
            onLogout={() => {
              setLogoutError(null)
              logoutMutation.mutate()
            }}
            isLoggingOut={logoutMutation.isPending}
            logoutError={logoutError}
          />
        </div>

        {/* The store control: inline in the top bar on desktop, a full-width
            selector row below it on mobile. Choosing a store navigates to that
            store's route (StoreContext.switchStore). */}
        <div className={`row-flx flx-a-c ${styles['app-shell__store']}`}>
          <StoreSwitcher onSelect={switchStore} />
        </div>
      </header>

      <main className={styles['app-shell__main']}>
        <Outlet />
      </main>
    </div>
  )
}
